import { pageSizeByDefault } from 'constants/constants';
import { FilterModel, ISort } from 'features/common/types';
import { advancedListFilters } from '../components/ProvidersDataList/constants';

const FILTER_KEYS = new Set(advancedListFilters.map((filter) => filter.key));
const RESERVED_PARAMS = new Set(['search', 'page', 'size', 'sort']);

export interface ProvidersListPersistedState {
	search: string;
	page: number;
	size: number;
	sortFields: ISort[];
	filterApplied: FilterModel[];
	advancedProviderId?: string;
}

export const PROVIDERS_LIST_DEFAULT_SORT: ISort[] = [{ field: 'fantasyName', order: 'asc' }];

export const PROVIDERS_LIST_STATE_KEY = 'providersListSearch';
const PROVIDERS_LIST_STORAGE_KEY = 'providers-list-search';

export const normalizeProvidersListSearch = (search: string): string =>
	search.startsWith('?') ? search.slice(1) : search;

export const toProvidersListSearchParam = (queryString: string): string =>
	queryString ? (queryString.startsWith('?') ? queryString : `?${queryString}`) : '';

export const saveProvidersListSearch = (search: string): void => {
	const normalized = toProvidersListSearchParam(normalizeProvidersListSearch(search));
	if (normalized) {
		sessionStorage.setItem(PROVIDERS_LIST_STORAGE_KEY, normalized);
	} else {
		sessionStorage.removeItem(PROVIDERS_LIST_STORAGE_KEY);
	}
};

export const readProvidersListSearch = (): string => sessionStorage.getItem(PROVIDERS_LIST_STORAGE_KEY) ?? '';

const filterDefinitionByKey = new Map(advancedListFilters.map((filter) => [filter.key, filter]));

const sortToParam = (sortFields: ISort[]): string | undefined => {
	const primary = sortFields[0];
	if (!primary) return undefined;
	return primary.order === 'desc' ? `-${primary.field}` : primary.field;
};

const sortFromParam = (value: string | null): ISort[] => {
	if (!value) return PROVIDERS_LIST_DEFAULT_SORT;
	if (value.startsWith('-')) {
		return [{ field: value.slice(1), order: 'desc' }];
	}
	return [{ field: value, order: 'asc' }];
};

export const parseProvidersListState = (search: string): ProvidersListPersistedState => {
	const params = new URLSearchParams(normalizeProvidersListSearch(search));
	const filterApplied: FilterModel[] = [];

	const advancedProviderId = params.get('id') ?? undefined;

	params.forEach((value, key) => {
		if (RESERVED_PARAMS.has(key) || key === 'id' || !FILTER_KEYS.has(key) || !value) return;
		const definition = filterDefinitionByKey.get(key);
		if (!definition) return;
		filterApplied.push({ ...definition, value });
	});

	return {
		search: params.get('search') ?? '',
		page: Math.max(1, Number(params.get('page')) || 1),
		size: Math.max(1, Number(params.get('size')) || pageSizeByDefault),
		sortFields: sortFromParam(params.get('sort')),
		filterApplied,
		advancedProviderId,
	};
};

export const buildProvidersListSearch = (state: {
	search: string;
	page: number;
	size: number;
	sortFields: ISort[];
	filterApplied: FilterModel[];
	advancedProviderId?: string;
}): string => {
	const params = new URLSearchParams();

	if (state.search.trim()) {
		params.set('search', state.search.trim());
	}

	if (state.page > 1) {
		params.set('page', String(state.page));
	}

	if (state.size !== pageSizeByDefault) {
		params.set('size', String(state.size));
	}

	const sortParam = sortToParam(state.sortFields);
	if (sortParam && sortParam !== 'fantasyName') {
		params.set('sort', sortParam);
	}

	state.filterApplied.forEach((filter) => {
		if (filter.value === undefined || filter.value === null || filter.value === '') return;
		params.set(filter.key, String(filter.value));
	});

	if (state.advancedProviderId) {
		params.set('id', state.advancedProviderId);
	}

	return params.toString();
};

export const providersListPathWithSearch = (search: string): string => {
	if (!search) return '/providers';
	return search.startsWith('?') ? `/providers${search}` : `/providers?${search}`;
};
